import { WX_ID_GSM } from "../../../store/WeatherContentsState";
import GsmUpper from "./GsmUpper";
import GsmGround from "./GsmGround";
import { _orderBy, calcPressureFromAlt, ft2m, translation } from "../../../utils/FPEUtils";
import LegendGsm from "../legend/LegendGsm";
import TimeController from "../helper/TimeController";
import Mutex from "../../../utils/Mutex";
import LegendGsmSub from "../legend/LegendGsmSub";

class Gsm {
  static NAME = WX_ID_GSM;
  static GSM_GROUND = `${Gsm.NAME}_Ground`;
  static GSM_UPPER = `${Gsm.NAME}_Upper`;
  static WIND_ARROW_NAME = `${Gsm.NAME}_WIND_ARROW`;
  static WIND_STREAM_NAME = `${Gsm.NAME}_WIND_STREAM`;
  static ISOTACH_CONTOUR_NAME = `${Gsm.NAME}_ISOTACH_CONTOUR`;
  static ISOTACH_GRADATION_NAME = `${Gsm.NAME}_ISOTACH_GRADATION`;
  static ISOTHERM_CONTOUR_NAME = `${Gsm.NAME}_ISOTHERM_CONTOUR`;
  static ISOTHERM_GRADATION_NAME = `${Gsm.NAME}_ISOTHERM_GRADATION`;
  static ISOTHERM_GRID_NAME = `${Gsm.NAME}_ISOTHERM_VALUE`;
  static GEO_POTENTIAL_HEIGHT_NAME = `${Gsm.NAME}_GEO_POTENTIAL_HEIGHT`;
  static ICING_PROB_NAME = `${Gsm.NAME}_ICING_PROB_NAME`;
  static PRECIPITATION_1H_MESH_NAME = `${Gsm.NAME}_PRECIPITATION_1H_MESH`;
  static PRECIPITATION_1H_CONTOUR_NAME = `${Gsm.NAME}_PRECIPITATION_1H_CONTOUR`;
  static PRECIPITATION_1H_GRID_NAME = `${Gsm.NAME}_PRECIPITATION_1H_GRID`;
  static RELATIVE_HUMIDITY_CONTOUR_NAME = `${Gsm.NAME}_RELATIVE_HUMIDITY_CONTOUR_NAME`;
  static RELATIVE_HUMIDITY_GRADATION_NAME = `${Gsm.NAME}_RELATIVE_HUMIDITY_GRADATION_NAME`;
  static GSM_UPPER_LEVEL = {
    "100": "FL530 / 100hPa",
    "150": "FL450 / 150hPa",
    "200": "FL390 / 200hPa",
    "250": "FL340 / 250hPa",
    "300": "FL300 / 300hPa",
    "400": "FL240 / 400hPa",
    "500": "FL180 / 500hPa",
    "600": "FL140 / 600hPa",
    "700": "FL100 / 700hPa",
    "800": "FL070 / 800hPa",
    "850": "FL050 / 850hPa",
    "900": "FL030 / 900hPa",
    "925": "FL025 / 925hPa",
    "950": "FL020 / 950hPa",
    "975": "FL010 / 975hPa",
    "1000": "FL005 / 1000hPa",
  };
  static GSM_GROUND_CONTENTS_KEYS = [
    Gsm.WIND_ARROW_NAME,
    Gsm.WIND_STREAM_NAME,
    Gsm.ISOTACH_CONTOUR_NAME,
    Gsm.ISOTACH_GRADATION_NAME,
    Gsm.ISOTHERM_CONTOUR_NAME,
    Gsm.ISOTHERM_GRADATION_NAME,
    Gsm.ISOTHERM_GRID_NAME,
    Gsm.ICING_PROB_NAME,
    Gsm.PRECIPITATION_1H_MESH_NAME,
    Gsm.PRECIPITATION_1H_CONTOUR_NAME,
    Gsm.PRECIPITATION_1H_GRID_NAME,
  ];
  static GSM_UPPER_CONTENTS_KEYS = [
    Gsm.WIND_ARROW_NAME,
    Gsm.WIND_STREAM_NAME,
    Gsm.ISOTACH_CONTOUR_NAME,
    Gsm.ISOTACH_GRADATION_NAME,
    Gsm.ISOTHERM_CONTOUR_NAME,
    Gsm.ISOTHERM_GRADATION_NAME,
    Gsm.ISOTHERM_GRID_NAME,
    Gsm.ICING_PROB_NAME,
    Gsm.GEO_POTENTIAL_HEIGHT_NAME,
    Gsm.RELATIVE_HUMIDITY_CONTOUR_NAME,
    Gsm.RELATIVE_HUMIDITY_GRADATION_NAME,
  ];
  static CONTENTS_VISIBLE_STATE_DEFAULT = {
    [Gsm.WIND_ARROW_NAME]: false,
    [Gsm.WIND_STREAM_NAME]: true,
    [Gsm.ISOTACH_CONTOUR_NAME]: true,
    [Gsm.ISOTACH_GRADATION_NAME]: false,
    [Gsm.ISOTHERM_CONTOUR_NAME]: true,
    [Gsm.ISOTHERM_GRADATION_NAME]: false,
    [Gsm.ISOTHERM_GRID_NAME]: false,
    [Gsm.GEO_POTENTIAL_HEIGHT_NAME]: true,
    [Gsm.ICING_PROB_NAME]: true,
    [Gsm.PRECIPITATION_1H_MESH_NAME]: true,
    [Gsm.PRECIPITATION_1H_CONTOUR_NAME]: true,
    [Gsm.PRECIPITATION_1H_GRID_NAME]: false,
    [Gsm.RELATIVE_HUMIDITY_CONTOUR_NAME]: true,
    [Gsm.RELATIVE_HUMIDITY_GRADATION_NAME]: false,
  };
  keyMap =  {
    [Gsm.WIND_ARROW_NAME]: {
      groundKey: GsmGround.WIND_ARROW_NAME,
      upperKey: GsmUpper.WIND_ARROW_NAME
    },
    [Gsm.WIND_STREAM_NAME]: {
      groundKey: GsmGround.WIND_STREAM_NAME,
      upperKey: GsmUpper.WIND_STREAM_NAME
    },
    [Gsm.ISOTACH_CONTOUR_NAME]: {
      groundKey: GsmGround.ISOTACH_CONTOUR_NAME,
      upperKey: GsmUpper.ISOTACH_CONTOUR_NAME
    },
    [Gsm.ISOTACH_GRADATION_NAME]: {
      groundKey: GsmGround.ISOTACH_GRADATION_NAME,
      upperKey: GsmUpper.ISOTACH_GRADATION_NAME
    },
    [Gsm.ISOTHERM_CONTOUR_NAME]: {
      groundKey: GsmGround.ISOTHERM_CONTOUR_NAME,
      upperKey: GsmUpper.ISOTHERM_CONTOUR_NAME
    },
    [Gsm.ISOTHERM_GRADATION_NAME]: {
      groundKey: GsmGround.ISOTHERM_GRADATION_NAME,
      upperKey: GsmUpper.ISOTHERM_GRADATION_NAME
    },
    [Gsm.ISOTHERM_GRID_NAME]: {
      groundKey: GsmGround.ISOTHERM_GRID_NAME,
      upperKey: GsmUpper.ISOTHERM_GRID_NAME
    },
    [Gsm.GEO_POTENTIAL_HEIGHT_NAME]: {
      groundKey: undefined,
      upperKey: GsmUpper.GEO_POTENTIAL_HEIGHT_NAME
    },
    [Gsm.ICING_PROB_NAME]: {
      groundKey: GsmGround.ICING_PROB_NAME,
      upperKey: GsmUpper.ICING_PROB_NAME
    },
    [Gsm.PRECIPITATION_1H_MESH_NAME]: {
      groundKey: GsmGround.PRECIPITATION_1H_MESH_NAME,
      upperKey: undefined
    },
    [Gsm.PRECIPITATION_1H_CONTOUR_NAME]: {
      groundKey: GsmGround.PRECIPITATION_1H_CONTOUR_NAME,
      upperKey: undefined
    },
    [Gsm.PRECIPITATION_1H_GRID_NAME]: {
      groundKey: GsmGround.PRECIPITATION_1H_GRID_NAME,
      upperKey: undefined
    },
    [Gsm.RELATIVE_HUMIDITY_CONTOUR_NAME]: {
      groundKey: undefined,
      upperKey: GsmUpper.RELATIVE_HUMIDITY_CONTOUR_NAME
    },
    [Gsm.RELATIVE_HUMIDITY_GRADATION_NAME]: {
      groundKey: undefined,
      upperKey: GsmUpper.RELATIVE_HUMIDITY_GRADATION_NAME
    },
  };
  viewGround = true;
  altitude = 0;
  level = '0';
  contentsVisibleState = {...Gsm.CONTENTS_VISIBLE_STATE_DEFAULT};
  //GSMタイムコントローラー
  gsmTimeController;
  //タイムコントローラーのリスト
  timeList;
  //自動更新フラグ
  autoRefresh = true;

  constructor(isDark) {
    this.gsmTimeController = new TimeController(WX_ID_GSM,{
      label: 'GSM',
      getTimeList: () => {
        if (this.timeList) {
          return this.timeList;
        }
        this._updateTimeList();
        return this.timeList;
      },
      updated: () => {
        this.dispatcher({type: 'SET_CONTENTS_TIME', payload: this.getTimeInformation()});
      },
      changeAutoRefresh: (autoFlg) => {
        this.setAutoRefresh(autoFlg);
      }
    });
    this.gsmUpper = new GsmUpper(isDark, this.gsmTimeController);
    this.gsmGround = new GsmGround(isDark, this.gsmTimeController);
    this.gsmUpper.setUpdateTimeFunc(this._updateTimeList.bind(this));
    this.gsmGround.setUpdateTimeFunc(this._updateTimeList.bind(this));
    this.drawLayerMutex = new Mutex();
  }

  setDispatcher(dispatcher) {
    this.dispatcher = dispatcher;
    const cascade = (action) => {
      if (action.type === 'SET_CONTENTS_TIME') {
        this.dispatcher({type: 'SET_CONTENTS_TIME', payload: this.getTimeInformation()});
      } else {
        return this.dispatcher(action);
      }
    };
    this.gsmUpper.setDispatcher(cascade);
    this.gsmGround.setDispatcher(cascade);
  }

  setModelDispatcher(dispatcher) {
    this.modelDispatcher = dispatcher;
  }

  async drawLayer(visible) {
    this.drawLayerMutex.runExclusion(async () => {
      this.visible = visible;

      if (visible) {
        //visible = falseの切り替えから行う(TimeController絡みの最適化）
        if(this.viewGround) {
          await Promise.all([
            this.gsmUpper.drawLayer(false),
            this.gsmGround.drawLayer(true)
          ]);
        } else {
          await Promise.all([
            this.gsmGround.drawLayer(false),
            this.gsmUpper.drawLayer(true)
          ]);
        }
      } else {
        await Promise.all([
          this.gsmGround.drawLayer(false),
          this.gsmUpper.drawLayer(false)
        ]);
      }
    });
  }

  getLayer() {
    return [...this.gsmUpper.getLayer(), ...this.gsmGround.getLayer()];
  }

  getTimeInformation() {
    let info;
    if(this.viewGround) {
      info = this.gsmGround.getTimeInformation();
    } else {
      info = this.gsmUpper.getTimeInformation();
    }
    info.id = Gsm.NAME;
    return info;
  }

  getLegend() {
    return {
      id: Gsm.NAME,
      label: 'GSM',
      component: LegendGsm,
      subComponent: LegendGsmSub,
      contents: this
    };
  }

  getContentsVisibleState() {
    return this.contentsVisibleState;
  }

  getIndividualAltitude() {
    return {
      id: Gsm.NAME,
      altitude: this.altitude,
      label: 'GSM',
      tooltip: this._altitudeTooltip
    };
  }

  getAltitudeLabel() {
    return this._altitudeTooltip(this.altitude);
  }

  applyIndividualAltitude(altitude) {
    if(this.altitude !== altitude) {
      const before = this.viewGround;
      if(altitude === 0) {
        this.viewGround = true;
      } else {
        this.viewGround = false;
        this.gsmUpper.applyIndividualAltitude(altitude);
      }

      if (before !== this.viewGround) {
        this.drawLayer(this.visible);
        this._updateTimeList();
      }

      this.altitude = altitude;
    }
  }

  applyElapsed(elapsed) {
    this.gsmGround.applyElapsed(elapsed);
    this.gsmUpper.applyElapsed(elapsed);
  }

  applyPlanETD(etd) {
    this.gsmGround.applyPlanETD(etd);
    this.gsmUpper.applyPlanETD(etd);
  }

  getTimeController() {
    return this.gsmTimeController;
  }

  setAutoRefresh(autoFlg) {
    this.autoRefresh = autoFlg;
    this.gsmGround.setAutoRefresh(autoFlg);
    this.gsmUpper.setAutoRefresh(autoFlg);
  }

  _altitudeTooltip(altitude) {
    if(altitude === 0) return translation('Ground');
    const gsm = [
      {fl: 530, mb: 100},
      {fl: 450, mb: 150},
      {fl: 390, mb: 200},
      {fl: 340, mb: 250},
      {fl: 300, mb: 300},
      {fl: 240, mb: 400},
      {fl: 180, mb: 500},
      {fl: 140, mb: 600},
      {fl: 100, mb: 700},
      {fl: 70, mb: 800},
      {fl: 50, mb: 850},
      {fl: 30, mb: 900},
      {fl: 25, mb: 925},
      {fl: 20, mb: 950},
      {fl: 10, mb: 975},
      {fl: 5, mb: 1000},
    ]

    const m = ft2m(altitude * 100);
    const mb = calcPressureFromAlt(m);
    const near = _orderBy(gsm, [
      o => {
        return Math.abs(mb - o.mb);
      },
      o => {
        return o;
      }
    ])[0];
    const fl = ('000' + near.fl).slice(-3);
    return `FL${fl} / ${near.mb}hPa`;
  }

  getDataType() {
    return this.viewGround ? Gsm.GSM_GROUND : Gsm.GSM_UPPER;
  }

  getContentsKeys() {
    return this.viewGround ? Gsm.GSM_GROUND_CONTENTS_KEYS : Gsm.GSM_UPPER_CONTENTS_KEYS;
  }

  resetContentsVisibleState(csAllOff) {
    if (Object.keys(this.contentsVisibleState)
      .filter(key => this.getContentsKeys(this.getDataType()).includes(key))
      .filter(key => this.contentsVisibleState[key]).length === 0 && csAllOff) {
      this.contentsVisibleState = {...Gsm.CONTENTS_VISIBLE_STATE_DEFAULT};
      this.gsmGround.resetContentsVisibleState();
      this.gsmUpper.resetContentsVisibleState();
      return true;
    }
    return false;
  }

  applyMapColor(mapColor, isDark) {
    this.gsmGround.applyMapColor(mapColor, isDark);
    this.gsmUpper.applyMapColor(mapColor, isDark);
  }

  async setVisible(visible, contents) {
    if (this.contentsVisibleState.hasOwnProperty(contents)) {
      this.contentsVisibleState[contents] = visible;

      const key = this.keyMap[contents];
      if (key?.groundKey !== undefined) {
        this.gsmGround.setVisible(visible, key.groundKey);
      }
      if (key?.upperKey !== undefined) {
        this.gsmUpper.setVisible(visible, key.upperKey);
      }
    }
    await this.modelDispatcher({type: 'SET_MODEL_CONTENTS_VISIBLE', payload: {targetName: contents, visible}});
  }

  _updateTimeList() {
    let timelist;
    if (this.viewGround) {
      timelist = this.gsmGround?.dh?.retained?.timelist?.timelist;
    } else {
      timelist = this.gsmUpper?.dh?.retained?.timelist?.timelist;
    }

    if (!timelist) {
      return;
    }

    //最新のbasetimeのvalidtimeを取得
    const latestBasetime = _orderBy(timelist, ['basetime'], ['desc'])[0];
    if (!latestBasetime.validtime) {
      return;
    }
    this.timeList = Array.from(latestBasetime.validtime).sort();

    if (this.autoRefresh) {
      if (this.viewGround) {
        this.gsmTimeController.setCurrentTime(this.gsmGround.validTime);
      } else {
        this.gsmTimeController.setCurrentTime(this.gsmUpper.validTime);
      }
    }
    this.gsmTimeController.adjustCurrentTime();
    this.gsmTimeController.invalidate();
  }

  getLevels() {
    const labels = Object.keys(Gsm.GSM_UPPER_LEVEL)
      .sort((a,b) => { return a - b;})
      .map(value => {
        return {
          value,
          label: Gsm.GSM_UPPER_LEVEL[value]
        };
      });
    labels.push({value: "0", label: translation('Ground')});
    return labels;
  }
  async setLevel(level) {
    this.modelDispatcher({type: 'SET_MODEL_LEVEL', payload: level});

    if (this.level === level) return;
    this.level = level;
    await this.setViewGround(level === "0");
    if (level !== "0") {
      this.gsmUpper.applyIndividualLevel(level, Gsm.GSM_UPPER_LEVEL[level]);
    }
  }
  getLevel() {
    return this.level;
  }
  async setViewGround(viewGround) {
    if (this.viewGround === viewGround) return;
    this.viewGround = viewGround;
    await this.drawLayer(this.visible);
    this._updateTimeList();
  }

  isElementDisableAtLevel(element, level) {
    // 地上は高度による要素の制限はない
    if (this.viewGround) return false;

    const elementName = this.keyMap[element]?.upperKey;
    if (elementName === undefined) return true;

    return this.gsmUpper.isElementDisableAtLevel(elementName, level)
  }
}

export default Gsm;
